import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, FileText, AlertTriangle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';
import { useAuth } from '../context/AuthContext';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { approvalsAPI } from '../services/api';

function formatCurrency(n) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
}

const REASON_LABELS = {
  invalid_documents: 'Invalid Documents',
  incorrect_information: 'Incorrect Information',
  relationship_proof_missing: 'Relationship Proof Missing',
  other: 'Other',
};

export default function Approvals() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [rejectForm, setRejectForm] = useState({ rejection_reason_code: 'invalid_documents', rejection_remarks: '' });
  const [showReject, setShowReject] = useState(false);
  const [processing, setProcessing] = useState(false);
  const debouncedSearch = useDebouncedValue(search);

  const loadList = () => {
    const isInitial = requests.length === 0;
    if (isInitial) setLoading(true);
    else setSearching(true);
    approvalsAPI.list({ status: filter || undefined, employee_name: debouncedSearch || undefined })
      .then(({ data }) => setRequests(data))
      .finally(() => {
        setLoading(false);
        setSearching(false);
      });
  };

  useEffect(() => { loadList(); }, [filter, debouncedSearch]);

  const openDetail = (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    setActionMessage(null);
    setShowReject(false);
    approvalsAPI.get(id)
      .then(({ data }) => setDetail(data))
      .finally(() => setDetailLoading(false));
  };

  const handleApprove = async () => {
    if (!selectedId || !confirm('Approve this request and update employee coverage?')) return;
    setProcessing(true);
    setActionMessage(null);
    try {
      const { data } = await approvalsAPI.approve(selectedId);
      setActionMessage(`Approved. Endorsement: ${data.endorsementNumber}`);
      loadList();
      openDetail(selectedId);
    } catch (err) {
      alert(err.response?.data?.error || 'Approval failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!rejectForm.rejection_remarks.trim()) {
      alert('Rejection remarks are required');
      return;
    }
    setProcessing(true);
    try {
      await approvalsAPI.reject(selectedId, rejectForm);
      setShowReject(false);
      setActionMessage('Request rejected. Employee has been notified.');
      loadList();
      openDetail(selectedId);
    } catch (err) {
      alert(err.response?.data?.error || 'Rejection failed');
    } finally {
      setProcessing(false);
    }
  };

  const downloadDoc = async (docId, fileName) => {
    const { data } = await approvalsAPI.downloadDocument(docId);
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !detail) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Family Enrollment Approvals</h1>
        <p className="text-slate-500 mt-1">Review employee family member requests (Phase 2)</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <select className="input-field max-w-xs" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
        <SearchInput
          className="max-w-xs"
          placeholder="Search employee name"
          value={search}
          onChange={setSearch}
        />
        {searching && <span className="text-sm text-slate-500 self-center">Updating…</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card overflow-x-auto">
          <h2 className="font-semibold mb-4">Requests</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 border-b text-left">
                <th className="pb-2">Request</th>
                <th className="pb-2">Employee</th>
                <th className="pb-2">Premium Δ</th>
                <th className="pb-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr
                  key={r.id}
                  className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${selectedId === r.id ? 'bg-primary-50' : ''}`}
                  onClick={() => openDetail(r.id)}
                >
                  <td className="py-2">
                    <div className="font-medium text-primary-700">{r.request_number}</div>
                    <div className="text-xs text-slate-400">{new Date(r.submitted_at).toLocaleDateString('en-IN')}</div>
                  </td>
                  <td className="py-2">{r.employee_name}</td>
                  <td className="py-2 font-medium">{formatCurrency(r.premium_difference)}</td>
                  <td className="py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${
                      r.status === 'pending' ? 'bg-amber-50 text-amber-700'
                        : r.status === 'approved' ? 'bg-green-50 text-green-700'
                          : 'bg-red-50 text-red-700'
                    }`}
                    >
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {requests.length === 0 && <p className="text-center py-8 text-slate-500">No requests found.</p>}
        </div>

        <div className="card">
          {!selectedId && <p className="text-slate-500">Select a request to review details.</p>}
          {selectedId && detailLoading && <LoadingSpinner />}
          {detail && !detailLoading && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">{detail.request_number}</h2>
                  <p className="text-slate-500">{detail.employee_name} · {detail.employee_code}</p>
                </div>
                <span className="px-2 py-1 rounded-full text-xs capitalize bg-slate-100">{detail.status}</span>
              </div>

              {actionMessage && (
                <div className="p-3 bg-green-50 text-green-800 rounded text-sm">{actionMessage}</div>
              )}

              {detail.policyExpired && detail.status === 'pending' && (
                <div className="p-3 bg-red-50 text-red-700 rounded text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Policy has expired. Approval is blocked until policy is renewed.
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-400 block">Policy</span>{detail.policy_number}</div>
                <div><span className="text-slate-400 block">Type</span><span className="capitalize">{detail.request_type}</span></div>
                <div><span className="text-slate-400 block">Current premium</span>{formatCurrency(detail.current_premium)}</div>
                <div><span className="text-slate-400 block">Proposed premium</span>{formatCurrency(detail.proposed_premium)}</div>
                <div><span className="text-slate-400 block">Difference</span><strong>{formatCurrency(detail.premium_difference)}</strong></div>
                <div><span className="text-slate-400 block">Submitted</span>{new Date(detail.submitted_at).toLocaleString('en-IN')}</div>
              </div>

              <div>
                <h3 className="font-medium text-sm mb-2">Current family members</h3>
                <ul className="text-sm space-y-1">
                  {(detail.familyMembers || []).map((m) => (
                    <li key={m.id} className="text-slate-600 capitalize">{m.member_name} ({m.relation?.replace(/_/g, ' ')})</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-sm mb-2">Requested changes</h3>
                <ul className="text-sm space-y-2">
                  {(detail.items || []).map((item) => (
                    <li key={item.id} className="p-2 bg-slate-50 rounded">
                      <span className="font-medium capitalize">{item.action}</span>: {item.member_name}
                      <span className="text-slate-500"> · {item.relation?.replace(/_/g, ' ')} · DOB {item.date_of_birth}</span>
                      {item.action === 'add' && (
                        <span className="block text-slate-600">Premium: {formatCurrency(parseFloat(item.pro_rata_premium) + parseFloat(item.gst_amount))}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {(detail.documents || []).length > 0 && (
                <div>
                  <h3 className="font-medium text-sm mb-2">Documents</h3>
                  <div className="space-y-1">
                    {detail.documents.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() => downloadDoc(doc.id, doc.file_name)}
                        className="text-primary-600 text-sm flex items-center gap-1 hover:underline"
                      >
                        <FileText className="w-4 h-4" />
                        {doc.file_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {detail.status === 'rejected' && (
                <div className="p-3 bg-red-50 rounded text-sm">
                  <strong>Rejection:</strong> {REASON_LABELS[detail.rejection_reason_code] || detail.rejection_reason_code}
                  <p className="mt-1">{detail.rejection_remarks}</p>
                </div>
              )}

              {isSuperAdmin && detail.status === 'pending' && (
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <button
                    type="button"
                    className="btn-primary flex items-center gap-2"
                    onClick={handleApprove}
                    disabled={processing || detail.policyExpired}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn-secondary text-red-600 border-red-200 flex items-center gap-2"
                    onClick={() => setShowReject(!showReject)}
                    disabled={processing}
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              )}

              {showReject && isSuperAdmin && (
                <form onSubmit={handleReject} className="p-4 bg-slate-50 rounded space-y-3">
                  <div>
                    <label className="label">Reason *</label>
                    <select
                      className="input-field"
                      value={rejectForm.rejection_reason_code}
                      onChange={(e) => setRejectForm({ ...rejectForm, rejection_reason_code: e.target.value })}
                    >
                      {Object.entries(REASON_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Remarks *</label>
                    <textarea
                      className="input-field min-h-[80px]"
                      required
                      value={rejectForm.rejection_remarks}
                      onChange={(e) => setRejectForm({ ...rejectForm, rejection_remarks: e.target.value })}
                      placeholder="Explain what the employee should correct..."
                    />
                  </div>
                  <button type="submit" className="btn-primary bg-red-600 hover:bg-red-700" disabled={processing}>
                    Confirm Rejection
                  </button>
                </form>
              )}

              {!isSuperAdmin && detail.status === 'pending' && (
                <p className="text-sm text-slate-500">Read-only view. Super Admin must approve or reject.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
